import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Alert, Typography } from '@mui/material';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from '../../stores';
import { UserRole, canAccessCop } from '../../types';
import LoginPage from '../../pages/LoginPage';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole;
  /** When true, allow analyst+ (hierarchy) or pf_external_guest (COP-only external group) */
  copAccess?: boolean;
  fallback?: ReactNode;
  redirectOnForbidden?: boolean;  // New prop to control redirect behavior
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  copAccess,
  fallback,
  redirectOnForbidden = false  // Default to false for backwards compatibility
}) => {
  const { user, isAuthenticated, hasPermission } = useAuthStore(
    useShallow((s) => ({
      user: s.user,
      isAuthenticated: s.isAuthenticated,
      hasPermission: s.hasPermission,
    }))
  );

  // Not authenticated (never signed in, or the session expired / was
  // signed out elsewhere). Render the same branded splash as /auth/login in
  // place (keeps the current URL so sign-in returns here) with a heading that
  // says why, rather than a bare alert.
  if (!isAuthenticated || !user) {
    return (
      <LoginPage
        heading="Session ended"
        message="You've been signed out or your session has expired. Sign in again to continue."
      />
    );
  }

  // COP: analyst+ or pf_external_guest
  if (copAccess && user.roles && !canAccessCop(user.roles)) {
    if (redirectOnForbidden && user.roles.includes('student')) {
      return <Navigate to="/itinerary" replace />;
    }
    if (fallback) {
      return <>{fallback}</>;
    }
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography sx={{ mb: 2 }}>
            You need analyst access or external guest COP access to open the COP dashboard.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Authenticated but insufficient permissions
  if (requiredRole && !hasPermission(requiredRole)) {
    // If redirect enabled and user is a student, redirect to itinerary
    if (redirectOnForbidden && user.roles?.includes('student')) {
      console.warn(`Access denied for student: Redirecting to /itinerary`);
      return <Navigate to="/itinerary" replace />;
    }
    if (redirectOnForbidden && user.roles?.includes('pf_external_guest')) {
      return <Navigate to="/cop" replace />;
    }
    
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography sx={{ mb: 2 }}>
            You need at least <strong>{requiredRole}</strong> role to access this feature.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your current roles: {user.roles.join(', ') || 'None'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Contact your administrator if you need additional access.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // All checks passed
  return <>{children}</>;
};

export default ProtectedRoute;
